import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import * as dealsAPI from '../../../apis/dealsAPI'
import type {
  CreateManualInvoiceReqBody,
  InvoiceEntity,
} from '../../../apis/dealsAPI'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'
import { useToast } from '../../../hooks/useToast'

import { dealQueryKey } from './useDealQuery'
import { invoicesQueryKey } from './useInvoicesQuery'

export interface CreateManualInvoiceVariables {
  dealId: string
  body: CreateManualInvoiceReqBody
}

export function useCreateManualInvoiceMutation(): UseMutationResult<
  InvoiceEntity,
  unknown,
  CreateManualInvoiceVariables
> {
  const queryClient = useQueryClient()
  const toast = useToast()
  const handleError = useApiErrorHandler()

  return useMutation<InvoiceEntity, unknown, CreateManualInvoiceVariables>(
    ({ dealId, body }) => dealsAPI.createManualInvoice(dealId, body),
    {
      onError: handleError,
      onSuccess: (invoice) => {
        toast.current?.show({
          severity: 'success',
          summary: 'Invoice added',
          detail: `${invoice.invoiceNumber} added to the deal.`,
          life: 3000,
        })
      },
      onSettled: (_data, _err, { dealId }) => {
        queryClient.invalidateQueries({ queryKey: invoicesQueryKey(dealId) })
        queryClient.invalidateQueries({ queryKey: dealQueryKey(dealId) })
      },
    },
  )
}
